/**
 * iter50 — Player-submitted team rankings modal (Phase 2).
 *
 * Two-column drag-and-drop UI:
 *   • LEFT  — UNRANKED teams (grouped at the bottom of the list)
 *   • RIGHT — RANKED teams in order from most to least desired (1, 2, 3, …)
 *
 * Players can drag any team between columns OR reorder within the ranked
 * column. The auto-draft honors this ranking in STRICT mode: on each snake
 * turn, the picking player gets their highest-ranked AVAILABLE team.
 *
 * Pre-draft only — once a draft exists, the modal becomes read-only.
 * Mobile-friendly: works with touch via PointerSensor.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
    closestCorners, useDroppable,
} from "@dnd-kit/core";
import {
    SortableContext, useSortable, arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trophy, Save, X, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import ChipStatusPill from "@/components/onui/ChipStatusPill";

const RANKED_DROPPABLE = "ranked-zone";
const UNRANKED_DROPPABLE = "unranked-zone";


// iter87.144 — Mobile-first tap-to-pick tile picker replacing the
// pre-draft ranking UX. Operator direction: "Need a better ranking than
// click and drag since this is a mobile UI. Can you make it like tiles
// and have all rankings pre draft picks be a highlight. Let's do top 3
// and they can dissect the tile [tap to deselect]. Instead of ranking
// 1-5 or Anything."
//
// Contract:
//   n          — cap (default 3); tapping while ranked.length === n is a no-op.
//   teams      — array of team objects (must carry id, name; seed optional).
//   ranked     — current ordered team-id list. First entry is 1st pick, etc.
//   onChange   — receives the next ordered list of team ids.
//   locked     — when true, tiles render read-only.
//
// Tap semantics:
//   • Unranked tile tapped → appended to end of `ranked` (fills the next open slot).
//   • Ranked tile tapped   → removed from `ranked`; entries after it shift up
//                             (so the numbering stays 1..n without gaps).
function TapToPickTiles({ n, teams, ranked, onChange, locked }) {
    const rankedSet = new Set(ranked);
    const sorted = [...teams].sort(
        (a, b) => (a.seed ?? 9999) - (b.seed ?? 9999) || a.name.localeCompare(b.name)
    );
    const handleTap = (teamId) => {
        if (locked) return;
        if (rankedSet.has(teamId)) {
            // Deselect + auto-shift the rest up.
            onChange(ranked.filter((id) => id !== teamId));
            return;
        }
        if (ranked.length >= n) return;
        onChange([...ranked, teamId]);
    };
    const filled = ranked.length;
    return (
        <div className="space-y-3" data-testid="rank-tiles">
            {/* Progress row — shows the top-3 slots so the operator sees
                which spots are still open at a glance. */}
            <div className="flex items-center justify-between text-[11px] font-display uppercase tracking-wide">
                <span className="text-zinc-500">
                    Tap {n} teams — first tap = #1
                </span>
                <span
                    data-testid="rank-tiles-counter"
                    className={filled === n ? "text-volt font-black" : "text-zinc-400"}
                >
                    {filled} / {n}
                </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {sorted.map((t) => {
                    const rankIdx = ranked.indexOf(t.id);
                    const isPicked = rankIdx !== -1;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            data-testid={`rank-tile-${t.id}`}
                            data-picked={isPicked}
                            onClick={() => handleTap(t.id)}
                            disabled={locked || (!isPicked && filled >= n)}
                            className={[
                                "relative flex flex-col items-start gap-1 p-3 rounded-xl border-2 text-left",
                                "transition-all duration-150 disabled:opacity-40",
                                isPicked
                                    ? "border-cyber bg-cyber/[0.08] shadow-[0_0_16px_2px_rgba(69,224,229,0.35)]"
                                    : "border-white/[0.10] bg-[#141414] hover:border-cyber/40",
                            ].join(" ")}
                            aria-pressed={isPicked}
                        >
                            {isPicked && (
                                <span
                                    data-testid={`rank-tile-badge-${t.id}`}
                                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-cyber text-black font-display font-black text-sm flex items-center justify-center shadow-[0_0_10px_rgba(69,224,229,0.6)]"
                                >
                                    {rankIdx + 1}
                                </span>
                            )}
                            <span className="text-sm font-black text-white leading-tight line-clamp-2">
                                {t.name}
                            </span>
                            {t.seed != null && (
                                <span className="text-[10px] text-zinc-500">
                                    Seed {t.seed}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {!locked && filled === n && (
                <p
                    data-testid="rank-tiles-full-note"
                    className="text-[11px] text-zinc-500 italic"
                >
                    Top {n} locked in — tap any highlighted tile to swap it out.
                </p>
            )}
        </div>
    );
}


// iter86.16 — Top-N mode uses N simple dropdowns instead of drag-and-drop.
// User feedback: the DnD board didn't render reliably on mobile; selects
// are native, keyboard/touch-proof, and unambiguous. Picking a team that's
// already in another slot SWAPS the two slots.
function TopNSelects({ n, teams, ranked, onChange, locked }) {
    const slots = Array.from({ length: n }, (_, i) => ranked[i] || "");
    const sorted = [...teams].sort((a, b) => (a.seed ?? 99) - (b.seed ?? 99) || a.name.localeCompare(b.name));
    const setSlot = (idx, teamId) => {
        const next = [...slots];
        if (teamId && next.includes(teamId)) {
            next[next.indexOf(teamId)] = next[idx] || "";
        }
        next[idx] = teamId;
        onChange(next);
    };
    return (
        <div className="space-y-3" data-testid="rank-topn-selects">
            {slots.map((val, i) => {
                const picked = !!val;
                return (
                    <div
                        key={i}
                        className={`flex items-center gap-3 rounded-xl border p-2 transition-colors ${
                            picked
                                ? "border-volt/50 bg-volt/[0.05]"
                                : "border-white/[0.08] bg-[#141414]"
                        }`}
                    >
                        <span
                            className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-black border ${
                                picked
                                    ? "bg-volt text-black border-black/40"
                                    : "bg-white/[0.06] text-zinc-500 border-white/10"
                            }`}
                        >
                            #{i + 1}
                        </span>
                        <select
                            value={val}
                            disabled={locked}
                            onChange={(e) => setSlot(i, e.target.value)}
                            data-testid={`rank-slot-${i + 1}-select`}
                            className="flex-1 min-w-0 bg-transparent border border-white/10 focus:border-cyber focus:outline-none rounded-md px-3 py-2.5 text-sm text-white disabled:opacity-60"
                        >
                            <option value="">— no pick —</option>
                            {sorted.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.flag ? `${t.flag} ` : ""}{t.name}{t.seed != null ? ` (tier ${t.seed})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                );
            })}
        </div>
    );
}


function SortableTeam({ team, rank, locked, columnPrefix }) {
    const sortableId = `${columnPrefix}:${team.id}`;
    const {
        attributes, listeners, setNodeRef, transform, transition, isDragging,
    } = useSortable({ id: sortableId, disabled: locked });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            data-testid={`rank-team-${team.id}`}
            className={`brutal-card-light p-3 flex items-center gap-3 ${
                locked ? "opacity-70" : "cursor-grab active:cursor-grabbing"
            }`}
            {...(locked ? {} : { ...attributes, ...listeners })}
        >
            {rank ? (
                <span className="pill bg-volt/20 border-volt text-volt text-xs font-bold w-7 h-7 flex items-center justify-center shrink-0" data-testid={`rank-team-position-${team.id}`}>
                    {rank}
                </span>
            ) : (
                <GripVertical className="w-4 h-4 text-zinc-600 shrink-0" />
            )}
            {team.flag && (
                <span className="text-lg shrink-0" aria-hidden>{team.flag}</span>
            )}
            <span className="font-bold text-sm truncate flex-1">{team.name}</span>
            {team.seed != null && (
                <span className="text-[10px] text-zinc-500 shrink-0">seed {team.seed}</span>
            )}
        </div>
    );
}


function Column({ id, title, icon: Icon, accent, teams, columnPrefix, emptyText, locked, showRank }) {
    const ids = teams.map(t => `${columnPrefix}:${t.id}`);
    // useDroppable makes the container itself a valid drop target even when
    // empty — without this, dragging a team into a column with 0 items has
    // no `over` entity to attach to, and the drop is silently lost.
    const { setNodeRef, isOver } = useDroppable({ id });
    return (
        <div className="space-y-2 min-w-0">
            <div className={`flex items-center gap-2 text-xs uppercase font-extrabold tracking-wider ${accent}`}>
                <Icon className="w-4 h-4" />
                {title}
                <span className="text-zinc-500 font-normal normal-case">({teams.length})</span>
            </div>
            <SortableContext id={id} items={ids} strategy={verticalListSortingStrategy}>
                <div
                    ref={setNodeRef}
                    data-testid={`rank-column-${id}`}
                    className={`space-y-2 min-h-[140px] p-2 rounded-md border-2 border-dashed transition-colors ${
                        isOver ? "border-cyber/70 bg-cyber/5" : (teams.length === 0 ? "border-zinc-700/60" : "border-transparent")
                    }`}
                >
                    {teams.map((t, idx) => (
                        <SortableTeam
                            key={t.id}
                            team={t}
                            rank={showRank ? idx + 1 : 0}
                            locked={locked}
                            columnPrefix={columnPrefix}
                        />
                    ))}
                    {teams.length === 0 && (
                        <p className="text-xs text-zinc-500 text-center py-6 select-none" data-testid={`rank-column-empty-${id}`}>
                            {emptyText}
                        </p>
                    )}
                </div>
            </SortableContext>
        </div>
    );
}


export default function RankTeamsModal({
    open, onOpenChange, leagueId, participantId, tournamentId, locked = false,
    prefillTopN = 0,
}) {
    // When `prefillTopN` > 0 we treat the modal as a strict top-N
    // ranker — the ranked column is capped at this many teams and
    // dropping a new team in displaces the bottom one back to the
    // unranked column. See `moveItem`.
    // iter87.144 — Mobile-first UX: tap-to-pick tiles for a top-3 ranker
    // is the default. Callers can still override via `prefillTopN` (e.g.
    // knockout templates that use top-5) but the classic drag-and-drop
    // 1-N board is retired for mobile viewports.
    const maxRanked = prefillTopN > 0 ? prefillTopN : 3;
    const [teamsById, setTeamsById] = useState({});
    const [ranked, setRanked] = useState([]);      // ordered team_ids
    const [unranked, setUnranked] = useState([]);  // ordered team_ids
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [dirty, setDirty] = useState(false);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    useEffect(() => {
        if (!open || !leagueId || !participantId) return;
        let cancelled = false;
        setLoading(true);
        api.getTeamRanking(leagueId, participantId, tournamentId).then(d => {
            if (cancelled) return;
            const byId = Object.fromEntries((d.teams || []).map(t => [t.id, t]));
            const savedRanking = (d.team_ranking || []).filter(tid => byId[tid]);
            const rankedSet = new Set(savedRanking);
            const fallback = (d.teams || [])
                .filter(t => !rankedSet.has(t.id))
                .map(t => t.id);
            // iter63z'''' — Top-N prefill for knockout-only templates.
            // When the player has NO saved ranking yet, seed the ranked
            // column with the lowest-`seed`-numbered teams so they only
            // have to confirm/re-order instead of starting from scratch.
            // `dirty` flips true so the Save button is enabled — saving
            // an empty ranking and re-opening still gives them a useful
            // starting point next time.
            if (prefillTopN > 0 && savedRanking.length === 0) {
                const sortedBySeed = [...(d.teams || [])]
                    .filter(t => t.seed != null)
                    .sort((a, b) => (a.seed ?? 9999) - (b.seed ?? 9999));
                const topIds = sortedBySeed.slice(0, prefillTopN).map(t => t.id);
                const topSet = new Set(topIds);
                setTeamsById(byId);
                setRanked(topIds);
                setUnranked((d.teams || []).filter(t => !topSet.has(t.id)).map(t => t.id));
                setDirty(true);
            } else {
                setTeamsById(byId);
                setRanked(savedRanking);
                setUnranked(fallback);
                setDirty(false);
            }
        }).catch(e => {
            toast.error(e.response?.data?.detail || "Couldn't load teams");
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [open, leagueId, participantId, tournamentId, prefillTopN]);

    const teamsList = useMemo(() => ({
        ranked: ranked.map(id => teamsById[id]).filter(Boolean),
        unranked: unranked.map(id => teamsById[id]).filter(Boolean),
    }), [ranked, unranked, teamsById]);

    const moveItem = (teamId, fromList, toList, toIndex) => {
        const fromArr = fromList === "ranked" ? [...ranked] : [...unranked];
        const toArr = toList === "ranked" ? [...ranked] : [...unranked];
        if (fromList === toList) {
            // Reorder within the same column
            const oldIdx = fromArr.indexOf(teamId);
            if (oldIdx === -1) return;
            const reordered = arrayMove(fromArr, oldIdx, Math.max(0, Math.min(toIndex, fromArr.length - 1)));
            if (fromList === "ranked") setRanked(reordered);
            else setUnranked(reordered);
        } else {
            // Cross-column move — remove from source, insert at destination.
            const oldIdx = fromArr.indexOf(teamId);
            if (oldIdx === -1) return;
            fromArr.splice(oldIdx, 1);
            const insertAt = Math.max(0, Math.min(toIndex, toArr.length));
            toArr.splice(insertAt, 0, teamId);
            // iter63z''''' — Cap the ranked column at `maxRanked` for
            // top-N modes (e.g., knockout-only top-5). When the cap is
            // exceeded by an incoming drop, displace the LAST team in
            // the ranked column back to the unranked column. This gives
            // a strict "always exactly N slots" UX without rejecting
            // the drop silently.
            let displaced = null;
            if (
                fromList === "unranked" && toList === "ranked"
                && maxRanked > 0 && toArr.length > maxRanked
            ) {
                displaced = toArr.pop();
            }
            if (fromList === "ranked" && toList === "unranked") {
                setRanked(fromArr);
                setUnranked(toArr);
            } else if (fromList === "unranked" && toList === "ranked") {
                let nextUnranked = fromArr;
                if (displaced) {
                    nextUnranked = [displaced, ...nextUnranked];
                }
                setUnranked(nextUnranked);
                setRanked(toArr);
            }
        }
        setDirty(true);
    };

    const parseId = (sortableId) => {
        const [col, ...rest] = (sortableId || "").split(":");
        return { col, teamId: rest.join(":") };
    };

    const handleDragStart = (e) => setActiveId(e.active.id);
    const handleDragEnd = (e) => {
        setActiveId(null);
        if (locked) return;
        const { active, over } = e;
        if (!over) return;

        const from = parseId(active.id);
        const overParsed = parseId(over.id);

        // Determine destination column. If `over.id` is one of the container
        // droppable ids (ranked-zone / unranked-zone), use those directly;
        // otherwise infer from the team prefix.
        let toCol = from.col;
        let toIndex = 0;
        if (over.id === RANKED_DROPPABLE) {
            toCol = "ranked";
            toIndex = ranked.length;
        } else if (over.id === UNRANKED_DROPPABLE) {
            toCol = "unranked";
            toIndex = unranked.length;
        } else {
            toCol = overParsed.col;
            const targetArr = toCol === "ranked" ? ranked : unranked;
            toIndex = targetArr.indexOf(overParsed.teamId);
            if (toIndex < 0) toIndex = targetArr.length;
        }
        moveItem(from.teamId, from.col, toCol, toIndex);
    };

    const save = async () => {
        if (locked || !dirty) return;
        setSaving(true);
        // iter86.16 — Top-N select mode can leave empty slots; save the
        // contiguous non-empty picks in order.
        const cleanRanked = ranked.filter(Boolean);
        try {
            await api.setTeamRanking(leagueId, participantId, cleanRanked, tournamentId);
            toast.success(
                cleanRanked.length === 0
                    ? "Cleared your ranking — the draft will use default tier balancing."
                    : `Saved your ranking (${cleanRanked.length} team${cleanRanked.length === 1 ? "" : "s"}).`,
            );
            setDirty(false);
            onOpenChange?.(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    const moveAllToRanked = () => {
        if (locked) return;
        setRanked([...ranked, ...unranked]);
        setUnranked([]);
        setDirty(true);
    };
    const clearRanking = () => {
        if (locked) return;
        setUnranked([...ranked, ...unranked]);
        setRanked([]);
        setDirty(true);
    };

    const activeTeam = activeId ? teamsById[parseId(activeId).teamId] : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="rank-teams-modal"
                className="max-w-3xl bg-obsidian border border-cyber/40 !rounded-2xl max-h-[90dvh] overflow-y-auto p-5 sm:p-6 space-y-4"
            >
                <DialogHeader className="space-y-2">
                    <DialogTitle className="flex items-center gap-2 font-display font-extrabold uppercase text-cyber text-lg sm:text-xl">
                        <Trophy className="w-5 h-5" /> Rank your top {maxRanked}
                        <ChipStatusPill status={locked ? "locked" : "live"} testId="rank-teams-modal-status" className="ml-1" />
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-zinc-400 leading-relaxed">
                        {/* iter87.144 — Copy updated for the tap-to-pick tiles.
                            The drag/swap language is retired. */}
                        Tap {maxRanked} teams in order — first tap is your #1 pick.
                        Tap any highlighted tile to swap it out. The auto-draft
                        gives you your highest-ranked available pick on each
                        snake turn; teams you skip fall back to default tier
                        balancing.
                        {locked && (
                            <span className="block mt-1 text-blaze" data-testid="rank-teams-locked-reason">
                                Rankings are locked — the tournament is closed to changes.
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <p className="text-zinc-500 text-sm py-8 text-center" data-testid="rank-loading">Loading teams…</p>
                ) : (
                    // iter87.144 — Tap-to-pick tiles replace both the DnD
                    // board and the TopNSelects dropdowns. Everything routes
                    // through this one mobile-first surface.
                    <TapToPickTiles
                        n={maxRanked}
                        teams={Object.values(teamsById)}
                        ranked={ranked}
                        locked={locked}
                        onChange={(next) => { setRanked(next); setDirty(true); }}
                    />
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-white/[0.06]">
                    <button
                        type="button"
                        data-testid="rank-cancel-btn"
                        onClick={() => onOpenChange?.(false)}
                        className="brutal-btn-ghost text-sm py-2"
                    >
                        {locked || !dirty ? "Close" : "Cancel"}
                    </button>
                    {!locked && (
                        <button
                            type="button"
                            data-testid="rank-save-btn"
                            onClick={save}
                            disabled={!dirty || saving}
                            className="brutal-btn-primary text-sm py-2 disabled:opacity-40"
                        >
                            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save ranking"}
                        </button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
